import React, { useState, useRef, useEffect } from 'react';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import TranslationText from '@Components/Utilities/TranslationText';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import translator from '@Components/Utilities/Translator';

const TextArea = styled('textarea')(() => ({
    fontFamily: 'Arial, sans-serif',
    fontSize: '13px',
    maxWidth: '100%',
    minWidth: '100%',
    minHeight: '75px',
    padding: '10px',
    lineHeight: '1.5',
    color: 'black',
    marginTop: '15px'
}));

const DashedButton = styled(Button)(() => ({
    border: '1px #ccc dashed',
    width: '100%',
    fontWeight: 'normal',
    fontSize: '12px',
    margin: '15px 0 15px 0',
    padding: '9px'       
}));

const BaseButton = styled(Button)(() => ({
    marginTop: '5px',
    marginBottom: '15px',
    padding: '8px 35px',
    fontSize: '13px',
    padding: '5px 35px'
}));

const SaveButton = styled(BaseButton)(({ theme }) => ({
    background: theme.palette.primary.main,
    fontWeight: '700'
}));

const CancelButton = styled(BaseButton)(() => ({
    background: 'none',
    fontWeight: 'normal',
    '&:hover': {
        background: 'none'
    },
}));

const LinkButton = styled(Button)(({ theme }) => {
    let isDarkMode = theme.palette.mode == 'dark';
    return {
        padding: '0',
        margin: '0',
        minWidth: '0',
        fontSize: '12px',
        color: (isDarkMode) ? theme.palette.primary.contrastText : theme.palette.common.black,
        fontWeight: 'normal',
        textDecoration: 'underline',
        '&:hover': {
            background: 'none',
            textDecoration: 'underline',
        }
    }
});

const CommentPreview = styled('div')(({ theme }) => {
    let isDarkMode = theme.palette.mode == 'dark';
    return {
        background: (isDarkMode) ? 'transparent' : '#f5f5f5',
        marginTop: '15px',
        marginBottom: '15px',
        padding: '15px',
        fontSize: '12px',
        textAlign: 'left',
        borderRadius: '5px',
        border: '1px #ddd dashed',
        '& .fa-times-circle': {
            display: 'inline-block',
            verticalAlign: 'text-bottom',
            paddingRight: '10px',
            fontSize: '14px',
            color: (isDarkMode) ? theme.palette.primary.contrastText : '#aaa',
            '&:hover': {
                cursor: 'pointer'
            }
        },
        '& .fa-pencil-alt': {
            marginLeft: '5px',
            marginRight: '3px'
        },
        '& .preview-comment': {
            wordWrap: 'break-word'
        }
    }
});

const WatchListComment = (props) => {
    const routes = useRecoilValue(atoms.routes);
    let { Comment: c = '' } = props;
    const [comment, setComment] = useState(c);
    const [previousComment, setPreviousComment] = useState(c);

    const getDefaultViewState = (value) => value ? 'preview' : 'add';    
    const [viewState, setViewState] = useState(getDefaultViewState(c));   
    const commentInputRef = useRef(null);

    useEffect(() => {
        if (viewState == "edit") {
            commentInputRef.current.focus();
        }
    }, [viewState]);

    const textAreaKeyPressHandler = (event) => {
        /* 27 is esc */
        if (event.keyCode === 27 && viewState == "edit") {
            cancelComment();
        }
    }

    const updateComment = (value) => { 
        setComment(value);

        sandhillsRequest({
            url: routes.EventWatchlistUpdateComments,
            method: 'post', 
            data: {
                "listingId": props.ListingID,
                "comment": value
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((result) => {
            if (result && result.success) {
                setPreviousComment(value);
            }
        });
    }

    const saveComment = () => {
        updateComment(comment);
        setViewState(getDefaultViewState(comment));
    }

    const deleteComment = () => {
        updateComment('');
        setViewState('add');
    }

    const cancelComment = () => {
        setComment(previousComment);
        setViewState(getDefaultViewState(previousComment));
    }

    return (
        <div className='watchlist-comment'>
            {viewState == "add" &&
                <DashedButton sx={{ borderRadius: '20px' }} variant="dashed" width="full" onClick={ () => setViewState('edit') }>
                    <i className="far fa-comment-alt"></i>&nbsp;&nbsp;
                    <TranslationText tKey='sandhills.clicktoaddapersonalizednote' />
                </DashedButton>
            }

            {viewState == "preview" &&
                <CommentPreview sx={{ background: props.IsDetailsPage ? '#fff' : '' }}>
                    <i aria-label={translator.translate('sandhills.delete')} role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); deleteComment(); } }} className="far fa-times-circle" onClick={deleteComment}></i>

                    <span className="watchlist-comment__preview">
                        {comment}
                    </span>

                    <i className="fas fa-pencil-alt"></i>

                    <LinkButton onClick={() => setViewState('edit')} variant="text" disableRipple>
                        <TranslationText tKey='sandhills.edit' />
                    </LinkButton>
                </CommentPreview>
            }

            {viewState == "edit" && 
                <div className="watchlist-comment__edit">
                    <TextArea
                        maxLength="200"
                        key={`comment-text-${props.ListingID}`}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        ref={commentInputRef}
                        onKeyDown={textAreaKeyPressHandler}
                    />
                    

                    <SaveButton onClick={saveComment}>
                        <TranslationText tKey='sandhills.savenote' />
                    </SaveButton>

                    <CancelButton onClick={cancelComment} variant="none">
                        <TranslationText tKey='sandhills.cancel' />
                    </CancelButton>
                </div>
            }
        </div>
    );
}

export default WatchListComment;